/**
 * Mobile-Responsive IdleWarningModal Component
 * Features:
 * - Bottom sheet on mobile (< 640px), centered dialog on desktop
 * - Safe-area support for notches and home indicators
 * - Focus trap and scroll lock
 * - Dark mode and reduced motion support
 * - Full accessibility compliance
 */

import React from 'react';
import useIdleWarningModalResponsive from './useIdleWarningModalResponsive';
import { IDLE_MODAL_STYLES } from './IdleWarningModalResponsive.styles';

export default function IdleWarningModalResponsive({ open, logoutAtMs, onStay, onLogout }) {
  const {
    seconds, dialogRef, firstBtnRef, lastBtnRef, onKeyDown
  } = useIdleWarningModalResponsive({ open, logoutAtMs, onLogout });

  if (!open) return null;

  return (
    <>
      {/* Modal Container */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="idle-title"
        aria-describedby="idle-desc"
        className="idle-modal-container"
        onKeyDown={onKeyDown}
      >
        {/* Backdrop */}
        <div className="idle-modal-backdrop" />

        {/* Container: centered on all screen sizes */}
        <div className="idle-modal-positioner">
          <div
            ref={dialogRef}
            className="idle-modal-content"
            style={{ maxWidth: 'min(92%, 28rem)' }}
          >
            {/* Header */}
            <div className="idle-modal-header">
              <div className="idle-modal-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <div className="idle-modal-title-section">
                <h2 id="idle-title" className="idle-modal-title">
                  Are you still there?
                </h2>
                <p className="idle-modal-subtitle">
                  We noticed you haven't been active for a while
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="idle-modal-body">
              <p id="idle-desc" className="idle-modal-description">
                You will be automatically signed out in{' '}
                <strong className="idle-countdown-text">{seconds}</strong> seconds due to inactivity.
                Click "Stay logged in" to continue your session.
              </p>

              {/* Countdown Display */}
              <div className="idle-countdown-container">
                <div className="idle-countdown-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                </div>
                <div className="idle-countdown-text-section">
                  <div className="idle-countdown-label">Time remaining</div>
                  <div className="idle-countdown-value">{seconds} seconds</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="idle-modal-actions">
              <button
                ref={firstBtnRef}
                onClick={onLogout}
                className="idle-btn idle-btn-secondary"
                data-idle-modal="logout-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16,17 21,12 16,7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Log out now
              </button>
              <button
                ref={lastBtnRef}
                onClick={onStay}
                className="idle-btn idle-btn-primary"
                data-idle-modal="stay-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Stay logged in
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{IDLE_MODAL_STYLES}</style>
    </>
  );
}
